import type { Message, Participant } from "@catmail/shared";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ImageOff } from "lucide-react";
import { useMemo, useState } from "react";

import { Button } from "@/components";
import { formatTime } from "@/lib/datetime";

import { hasInlineImages, loadInlineImages } from "../../lib/inlineImages";
import { buildMailDocument, sanitizeMailHtml } from "../../lib/sanitizeMail";

type HtmlMailViewerProps = {
  message: Message;
  sender: Participant | null;
  /** 서버가 내려준 원문 HTML */
  html: string;
  onClose: () => void;
};

/** 시안 07 · HTML 메일이 왔을 때 */
export function HtmlMailViewer({
  message,
  sender,
  html,
  onClose,
}: HtmlMailViewerProps) {
  const [allowRemoteImages, setAllowRemoteImages] = useState(false);

  // 본문에 박힌 첨부 이미지. 원격 이미지와 달리 밖으로 나가지 않으므로
  // 사용자가 허락하기를 기다리지 않고 바로 받아 채운다.
  const inlineQuery = useQuery({
    queryKey: ["inline-images", message.id],
    queryFn: () => loadInlineImages(message.attachments),
    enabled: hasInlineImages(message.attachments),
    // 같은 메일을 다시 열 때 또 받을 이유가 없다. 내용이 바뀌지 않는다.
    staleTime: Infinity,
  });

  const inlineImages = inlineQuery.data;

  const { document: srcDoc, blockedCount } = useMemo(() => {
    const sanitized = sanitizeMailHtml(html, allowRemoteImages, inlineImages);
    return {
      document: buildMailDocument(sanitized.html, allowRemoteImages),
      blockedCount: sanitized.blockedCount,
    };
  }, [html, allowRemoteImages, inlineImages]);

  return (
    <div className="bg-paper flex flex-1 flex-col">
      <header className="border-line flex h-18 shrink-0 items-center gap-2 border-b px-3 sm:gap-3 sm:px-6.5">
        <Button variant="ghost" size="sm" onClick={onClose}>
          <ArrowLeft className="size-4" />
          채팅으로
        </Button>

        <div className="flex min-w-0 flex-col gap-px">
          <span className="text-ink line-clamp-1-ko text-[15px] font-semibold">
            {message.subject}
          </span>
          <span className="text-muted-2 line-clamp-1-ko text-xs">
            {sender?.displayName ?? "보낸사람"} · {formatTime(message.sentAt)}
          </span>
        </div>
      </header>

      <div className="flex-1 overflow-hidden bg-viewport p-2.5 sm:p-4.5">
        <iframe
          key={String(allowRemoteImages)}
          title={`${message.subject} 원문`}
          srcDoc={srcDoc}
          // 스크립트도, 같은 출처도 주지 않는다. 링크를 새 창으로 여는 권한만 준다.
          sandbox="allow-popups allow-popups-to-escape-sandbox"
          referrerPolicy="no-referrer"
          className="bg-surface size-full border-0"
        />
      </div>

      <footer className="border-line-soft flex shrink-0 flex-wrap items-center gap-2.5 border-t px-4 py-3 sm:px-5 sm:py-3.5">
        <ImageOff className="text-muted-3 size-3.5 shrink-0" />
        <p className="text-muted-2 flex-1 text-xs">
          {allowRemoteImages
            ? "외부 이미지를 불러왔어요. 보낸 쪽에서 열람 사실을 알 수 있어요."
            : blockedCount > 0
              ? `외부 이미지 ${blockedCount}개를 막아뒀어요. 추적에 쓰일 수 있어요.`
              : "외부 이미지는 열 때만 불러와요"}
        </p>

        {blockedCount > 0 && !allowRemoteImages ? (
          <Button
            variant="outline"
            size="pill"
            onClick={() => setAllowRemoteImages(true)}
          >
            이미지 불러오기
          </Button>
        ) : null}
      </footer>
    </div>
  );
}
